import type { ToneNotation } from "@kensio/pinyinjs";

import type { CopyFlavours } from "./clipboard.js";
import { plainFlavours } from "./copy-html.js";
import type { SegmentedWord } from "./segmented-words.js";
import { markPinyinWord } from "./syllable-text.js";
import { frequencyBandOf } from "./word-frequency.js";

/**
 * The word list as something to paste.
 *
 * The same four columns the table has, out of the same words and the same
 * bands, so that what somebody pastes is the list they were looking at. Apart
 * from `segmenter-rows.ts` because a table cell and a line of text have almost
 * nothing in common beyond that: one is markup a browser lays out and the
 * other is what survives being pasted into a program that knows nothing about
 * this page.
 */

/**
 * The list as tab separated text, for a spreadsheet or a flashcard deck.
 *
 * Tabs rather than commas, because a comma inside a gloss would need quoting
 * and every program that takes a pasted table takes tabs. A header row, so
 * that four unlabelled columns arriving in somebody's spreadsheet still say
 * what they are.
 */
export function wordsAsText(
  words: readonly SegmentedWord[],
  notation: ToneNotation,
): string {
  const rows = words.map((word) =>
    [
      word.text,
      markPinyinWord(word.reading, notation).join(""),
      String(word.count),
      frequencyBandOf(word.frequency)?.name ?? "",
    ].join("\t"),
  );

  return ["Word\tPinyin\tCount\tHow common", ...rows].join("\n");
}

/**
 * The list as something to put on the clipboard, or nothing to copy.
 *
 * Undefined for an empty list rather than a header row on its own, so that a
 * copy button pressed before anything has been read does nothing instead of
 * putting four column names on the clipboard.
 */
export function wordsCopy(
  words: readonly SegmentedWord[],
  notation: ToneNotation,
): CopyFlavours | undefined {
  return words.length === 0
    ? undefined
    : plainFlavours(wordsAsText(words, notation));
}
